import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Bot, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const STATS = [
  { big: "100", small: "leads promised in 90 days" },
  { big: "25 yrs", small: "studying the craft" },
  { big: "80+", small: "home-service companies" },
  { big: "4 → 40", small: "leads/mo, one client" },
];

const PHASES = [
  {
    tag: "Phase 1",
    title: "The 90-Day Repair",
    meta: "One-time · 90 days",
    body: "We diagnose your whole web presence, repair every crack, and build what's missing — then run it for 90 days until it's producing. Backed by the 100-lead promise.",
    price: "$500 – $15,000",
    priceNote: "priced to how much is broken",
  },
  {
    tag: "Phase 2",
    title: "The Retainer",
    meta: "Ongoing · optional · month-to-month",
    body: "Once it's repaired and producing, you keep the engine tuned and running. Leave whenever you want — nothing's locked in, and everything stays yours.",
    price: "$999/mo",
    priceNote: "+ $900/mo min ad spend",
  },
];

const TIERS = [
  {
    step: "Step 1 · Website",
    name: "The conversion-built website",
    price: "$3,500",
    period: "one-time",
    features: [
      "Custom website built to convert — on your own domain, you own it",
      "Trust signals, before-and-afters, reviews, click-to-call, quote form",
      "Google Business Profile optimized",
      "Local SEO foundation",
      "One-time repair. No monthly.",
    ],
    note: "For companies whose presence just needs to be worth landing on.",
  },
  {
    step: "Step 2 · Cross-Web Repair",
    name: "The full 90-day system",
    price: "$6,500",
    period: "one-time · 90 days",
    featured: true,
    promise: "100 leads in 90 days — or we keep working free",
    features: [
      "Conversion-built website + Google Business Profile",
      "GEO / AI-visibility — show up when people ask AI",
      "Managed Google Ads campaign, run daily by Todd",
      "Integrated local SEO working alongside the ads",
      "Plain-English lead tracking — every lead, its source",
      "Weekly check-in call",
    ],
    note: "You fund a minimum $900/mo ad budget on your own account. The $6,500 covers the 90-day repair + management.",
  },
  {
    step: "Step 3 · Full Market Build",
    name: "Own the whole metro",
    price: "$15,000",
    period: "one-time",
    features: [
      "Everything in Cross-Web Repair, plus:",
      "Full SEO authority build — rankings that keep producing after the ads",
      "Aggressive content velocity — video, articles, landing pages",
      "Remarketing across the metro",
      "Multi-location targeting + dedicated strategist",
    ],
    note: "Leads now, plus an engine that compounds for years.",
  },
];

export default function CrossWebRepairPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main data-testid="cross-web-repair-page">
      <Seo
        title="Cross-Web Repair Program — ZD Design"
        description="The Cross-Web Repair Program: we diagnose your whole web presence, repair every crack, and run it for 90 days — 100 leads in 90 days or we keep working free. Website, Google Ads, GEO and local SEO, all on accounts you own."
        path="/cross-web-repair"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 lg:pr-[400px] xl:pr-[520px]">
          <img
            src="/img/cross-web-repair-illustration.webp"
            alt="Cross-web repair illustration"
            data-testid="repair-hero-art"
            className="pointer-events-none absolute right-2 top-1/2 hidden w-[390px] -translate-y-1/2 select-none rounded-tr-[2.5rem] lg:block xl:right-8 xl:w-[480px]"
          />
          <FadeUp>
            <Eyebrow>Foundation &amp; structural repair</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              The Cross-Web{" "}
              <span className="text-flame">Repair Program</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              <strong className="font-bold text-slate-900">
                100 new leads in 90 days
              </strong>{" "}
              — from a web presence diagnosed, repaired, and run by a 25-year
              expert, on accounts you own. Because most companies aren't
              missing a website — they've got one that's broken and bringing in
              nothing. Not a template. Not an AI spitting out a site. A
              craftsman who's done this for 80+ home-service companies.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-3 md:grid-cols-4">
              {STATS.map((s, i) => (
                <div
                  key={s.big}
                  data-testid={`repair-stat-${i}`}
                  className="glass rounded-2xl border border-slate-200 p-5"
                >
                  <p className="font-display text-3xl font-bold text-slate-900">
                    {s.big}
                  </p>
                  <p className="mt-1 text-base leading-snug text-faint">{s.small}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Why it's a repair</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              Most companies aren't missing a website. They've got one that
              isn't working.
            </h2>
          </FadeUp>
          <FadeUp delay={0.08}>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-mist md:text-xl">
              You already spent the money — on a site, on "SEO," on ads — and
              the phone still isn't ringing. That's not a build problem, it's
              a <strong className="font-bold text-slate-900">repair</strong>{" "}
              problem: a web presence with cracks in it, quietly losing you
              jobs you never hear about. We diagnose the whole system, repair
              every crack, and build what's missing — then run it until it
              produces.
            </p>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-mist md:text-xl">
              <strong className="font-bold text-slate-900">
                This is for the owner who already knows they need this
                handled
              </strong>{" "}
              — or knows what they've got is broken — and wants more out of
              the money and effort they're already putting in. If that's you,
              we just make it work.
            </p>
          </FadeUp>
          <FadeUp delay={0.16}>
            <div
              data-testid="repair-ai-note"
              className="mt-12 flex flex-col gap-5 rounded-2xl border border-flame/30 bg-coal/60 p-8 md:flex-row md:items-center"
            >
              <div className="glass flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200">
                <Bot className="h-6 w-6 text-flame" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  He uses AI. He doesn't let AI do the work.
                </p>
                <p className="mt-3 max-w-3xl text-lg leading-relaxed text-mist">
                  AI makes a 25-year expert faster — it doesn't replace him.
                  Every repair is done by someone who's studied this craft for
                  a quarter century and worked hand-in-hand with 80+ owners.
                  That's the difference between hiring Todd and hiring a kid
                  with a chatbot spitting out a website.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              A 90-day repair. Then you keep it running.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mist">
              Two clean phases. First the whole system gets repaired and
              proven with real leads. Then — only if you want it — you keep
              the engine running on a simple monthly. Everything stays on
              accounts you own, the whole way through.
            </p>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
            {PHASES.map((p, i) => (
              <FadeUp key={p.tag} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`repair-phase-${i + 1}`}
                  className="glass h-full rounded-2xl border border-slate-200 p-8 transition-colors duration-300 hover:border-flame/40"
                >
                  <span className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                    {p.tag}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900">
                    {p.title}
                  </h3>
                  <p className="mt-1 font-mono text-base text-faint">{p.meta}</p>
                  <p className="mt-4 text-lg leading-relaxed text-mist">{p.body}</p>
                  <p className="mt-6 font-display text-2xl font-bold text-slate-900">
                    {p.price}
                  </p>
                  <p className="mt-1 text-base text-faint">{p.priceNote}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Phase 1 · The 90-day repair</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              Repair what's broken. Build what's missing.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mist">
              Start where you are — a light fix or the full repair — and own
              all of it, on your accounts, not rented from an agency you
              can't leave.
            </p>
          </FadeUp>

          <FadeUp delay={0.06}>
            <div
              data-testid="repair-tier-geo"
              className="mt-10 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-coal/60 p-6 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <p className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                  Light fix · GEO Revamp
                </p>
                <p className="mt-2 max-w-2xl text-lg leading-relaxed text-slate-700">
                  You've already got a decent site. We make you show up when a
                  homeowner asks Google's AI or ChatGPT for foundation help in
                  your area. The cheapest way onto the new search map.
                </p>
              </div>
              <div className="shrink-0">
                <p className="font-display text-3xl font-bold text-slate-900">$500</p>
                <p className="text-base text-faint">one-time</p>
              </div>
            </div>
          </FadeUp>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {TIERS.map((t, i) => (
              <FadeUp key={t.step} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`repair-tier-${i + 1}`}
                  className={`relative flex h-full flex-col rounded-2xl border p-8 transition-colors duration-300 ${
                    t.featured
                      ? "border-flame bg-coal/70 shadow-[0_0_40px_rgba(255,85,0,0.12)]"
                      : "glass border-slate-200 hover:border-flame/40"
                  }`}
                >
                  {t.featured ? (
                    <span
                      data-testid="repair-tier-featured-badge"
                      className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-flame px-4 py-1 font-mono text-[13px] font-semibold uppercase tracking-[0.15em] text-obsidian"
                    >
                      Most companies pick this
                    </span>
                  ) : null}
                  <p className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                    {t.step}
                  </p>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900">
                    {t.name}
                  </h3>
                  <p className="mt-4 font-display text-4xl font-bold text-slate-900">
                    {t.price}
                  </p>
                  <p className="mt-1 text-base text-faint">{t.period}</p>
                  {t.promise ? (
                    <p className="mt-4 flex items-start gap-2 rounded-xl border border-flame/30 bg-flame/5 px-3 py-2 text-base font-semibold leading-snug text-slate-900">
                      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                      {t.promise}
                    </p>
                  ) : null}
                  <ul className="mt-5 flex-1 space-y-3">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-lg leading-relaxed text-mist">
                        <ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-flame" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 border-t border-slate-200 pt-4 text-base leading-relaxed text-faint">
                    {t.note}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.1}>
            <p className="mx-auto mt-10 max-w-3xl text-lg leading-relaxed text-mist">
              For scope: most agencies charge{" "}
              <strong className="font-bold text-slate-900">
                $2,500–$5,000 a month on 12-month contracts
              </strong>{" "}
              — $30K–$60K a year, forever, on accounts you don't own. The
              repair is a one-time 90-day program on accounts you keep.{" "}
              <strong className="font-bold text-slate-900">
                No lock-in. No long contract. And we never take a percentage
                of your ad spend
              </strong>{" "}
              — you fund your budget, we run it.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>The Promise</Eyebrow>
            <h2
              data-testid="repair-promise"
              className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl"
            >
              100 leads in 90 days —{" "}
              <span className="text-flame">
                or we keep working, free, until you hit it.
              </span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.08}>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-mist md:text-xl">
              Backed by 80+ home-service companies, a client we took from 4
              leads a month to 40, and 25 years of a craft studied at an
              intimate, hands-on level.
            </p>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-mist">
              Conditioned on you funding the agreed minimum{" "}
              <strong className="font-bold text-slate-900">$900/mo ad budget</strong>{" "}
              on your own account and answering your leads promptly —
              speed-to-lead is what turns a lead into a booked job. If we
              don't hit 100 leads by day 90, we keep managing your campaign at
              no charge —{" "}
              <strong className="font-bold text-slate-900">we waive our $999/mo fee</strong>{" "}
              — until you do. You keep covering only the minimum $900/mo ad
              spend (or cancel); the free work is our management, never your
              media. We don't promise a #1 Google ranking: nobody controls
              Google's algorithm, and any agency that "guarantees rankings" is
              selling you the exact thing that burned you last time. What we
              promise is 100 leads — and we don't stop until you have them.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <div className="glass rounded-2xl border border-slate-200 p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Ready to find your gaps?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="repair-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <Link
                to="/ai-answer-checklist"
                data-testid="repair-cta-checklist-link"
                className="mt-5 inline-flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.18em] text-slate-300 underline decoration-flame/60 underline-offset-4 transition-colors hover:text-flame"
              >
                Or get the free AI-answers checklist first
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
